import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Trash2, ShoppingCart, ChevronDown, ChevronUp,
  CheckCircle, Clock, Package, Truck, CheckCheck,
  XCircle, Printer, ArrowRight, Eye,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import OrderLabelModal from '../../components/admin/OrderLabelModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending:    { label: 'Da confermare',  short: 'Conferma',    bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-400',  step: 0 },
  to_prepare: { label: 'Da preparare',   short: 'Da prep.',    bg: 'bg-blue-100',   text: 'text-blue-800',   dot: 'bg-blue-500',    step: 1 },
  preparing:  { label: 'In prep.',       short: 'In prep.',    bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-400',  step: 2 },
  ready:      { label: 'Pronto',         short: 'Pronto',      bg: 'bg-teal-100',   text: 'text-teal-800',   dot: 'bg-teal-400',    step: 3 },
  shipped:    { label: 'Spedito',        short: 'Spedito',     bg: 'bg-indigo-100', text: 'text-indigo-800', dot: 'bg-indigo-400',  step: 4 },
  completed:  { label: 'Completato',     short: 'Fatto',       bg: 'bg-green-100',  text: 'text-green-800',  dot: 'bg-green-400',   step: 5 },
  cancelled:  { label: 'Annullato',      short: 'Annullato',   bg: 'bg-red-100',    text: 'text-red-800',    dot: 'bg-red-400',     step: -1 },
};

const PIPELINE = ['pending','to_prepare','preparing','ready','shipped','completed'];

const STATUS_ACTIONS = {
  pending:    { next: 'to_prepare', label: 'Conferma e prepara',  icon: CheckCircle },
  to_prepare: { next: 'preparing',  label: 'Inizia preparazione', icon: Clock       },
  preparing:  { next: 'ready',      label: 'Segna come pronto',   icon: Package     },
  ready:      { next: 'shipped',    label: 'Spedito',             icon: Truck       },
  shipped:    { next: 'completed',  label: 'Completa',            icon: CheckCheck  },
};

const SHOW_LABEL_ON = ['to_prepare', 'shipped'];
const SHOW_WA_ON    = ['to_prepare'];

const FILTER_TABS = [
  { key: 'all',        label: 'Tutti'        },
  { key: 'pending',    label: 'Da confermare' },
  { key: 'to_prepare', label: 'Da preparare' },
  { key: 'preparing',  label: 'In prep.'     },
  { key: 'ready',      label: 'Pronto'       },
  { key: 'shipped',    label: 'Spedito'      },
  { key: 'completed',  label: 'Completato'   },
  { key: 'cancelled',  label: 'Annullato'    },
];

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}
function fmtPrice(p) {
  if (!p || p === 0) return '—';
  return `€ ${Number(p).toFixed(2).replace('.', ',')}`;
}
function parseItems(items) {
  if (!items) return [];
  if (Array.isArray(items)) return items;
  if (typeof items === 'string') { try { const p = JSON.parse(items); if (Array.isArray(p)) return p; if (typeof p === 'string') { const p2 = JSON.parse(p); return Array.isArray(p2) ? p2 : []; } } catch { return []; } }
  return [];
}

function StatusBadge({ status }) {
  const sc = STATUS_CONFIG[status] || { label: status, bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-400' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold font-body ${sc.bg} ${sc.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sc.dot}`} />
      {sc.label}
    </span>
  );
}

// Colori per ogni step — usati sia nella pipeline che nei pulsanti filtro
const STEP_COLORS = {
  all:        { full: '#2d5a27', light: '#e8f0e8', text: '#2d5a27' }, // primary
  pending:    { full: '#ca8a04', light: '#fef9c3', text: '#92400e' }, // yellow
  to_prepare: { full: '#2563eb', light: '#dbeafe', text: '#1e40af' }, // blue
  preparing:  { full: '#9333ea', light: '#f3e8ff', text: '#6b21a8' }, // purple
  ready:      { full: '#0d9488', light: '#ccfbf1', text: '#134e4a' }, // teal
  shipped:    { full: '#4f46e5', light: '#e0e7ff', text: '#3730a3' }, // indigo
  completed:  { full: '#16a34a', light: '#dcfce7', text: '#14532d' }, // green
  cancelled:  { full: '#dc2626', light: '#fee2e2', text: '#991b1b' }, // red
};

// Pipeline visivo mini — ogni pallino ha il suo colore specifico
function MiniPipeline({ status }) {
  if (status === 'cancelled') return (
    <span className="text-xs text-red-400 font-body italic">Annullato</span>
  );
  const currentStep = STATUS_CONFIG[status]?.step ?? 0;
  return (
    <div className="flex items-center gap-0.5">
      {PIPELINE.map((s, i) => {
        const done    = i < currentStep;
        const current = i === currentStep;
        const colors  = STEP_COLORS[s];
        const bgColor = (done || current) ? colors.full : colors.light;
        const size    = current ? 10 : 7;
        return (
          <div key={s} className="flex items-center gap-0.5">
            <div
              style={{ width: size, height: size, borderRadius: '50%', background: bgColor, flexShrink: 0, transition: 'all 0.3s', boxShadow: current ? `0 0 0 2px ${colors.full}40` : 'none' }}
              title={STATUS_CONFIG[s]?.label}
            />
            {i < PIPELINE.length - 1 && (
              <div style={{ width: 10, height: 1, background: done ? colors.full : '#e5e7eb', flexShrink: 0 }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function AdminOrdini() {
  const [orders,     setOrders]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState('all');
  const [expandedId, setExpanded]   = useState(null);
  const [deleteId,   setDeleteId]   = useState(null);
  const [updatingId, setUpdating]   = useState(null);
  const [labelState, setLabelState] = useState(null);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const res = await axios.get(`${API}/orders`, { params });
      setOrders(res.data);
    } catch { toast.error('Errore nel caricamento degli ordini'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchOrders(); }, [filter]); // eslint-disable-line

  const changeStatus = async (orderId, newStatus) => {
    setUpdating(orderId);
    try {
      const res = await axios.put(`${API}/orders/${orderId}`, { status: newStatus });
      setOrders(prev => prev.map(o => o.id === orderId ? res.data : o));
      toast.success('Stato aggiornato');
      return res.data;
    } catch { toast.error("Errore nell'aggiornamento"); return false; }
    finally { setUpdating(null); }
  };

  const handleAdvance = async (order) => {
    const action = STATUS_ACTIONS[order.status];
    if (!action) return;
    const updated = await changeStatus(order.id, action.next);
    if (updated && SHOW_LABEL_ON.includes(action.next)) {
      setLabelState({ order: updated, showWhatsApp: SHOW_WA_ON.includes(action.next) });
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/orders/${id}`);
      setOrders(prev => prev.filter(o => o.id !== id));
      toast.success('Ordine eliminato');
      setDeleteId(null);
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  const countMap = orders.reduce((acc, o) => { acc[o.status] = (acc[o.status] || 0) + 1; return acc; }, {});
  const activeCount = orders.filter(o => !['completed','cancelled'].includes(o.status)).length;

  return (
    <div className="min-w-0 w-full">

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-heading text-xl font-bold text-gray-900">Ordini</h2>
          <p className="font-body text-gray-500 text-xs mt-0.5">{orders.length} totali · {activeCount} attivi</p>
        </div>
      </div>

      {/* Filtri colorati — ogni pulsante usa il colore del suo stato */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {FILTER_TABS.map(tab => {
          const cnt      = tab.key === 'all' ? orders.length : (countMap[tab.key] || 0);
          const isActive = filter === tab.key;
          const colors   = STEP_COLORS[tab.key] || STEP_COLORS.all;
          return (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              style={isActive
                ? { background: colors.full, color: '#fff', borderColor: colors.full }
                : { background: colors.light, color: colors.text, borderColor: 'transparent' }
              }
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg font-body text-xs font-semibold transition-all border"
            >
              {tab.label}
              {cnt > 0 && (
                <span style={isActive
                  ? { background: 'rgba(255,255,255,0.25)', color: '#fff' }
                  : { background: 'rgba(0,0,0,0.08)', color: colors.text }
                } className="text-xs font-bold px-1 rounded-full leading-4">
                  {cnt}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => <div key={i} className="bg-white rounded-xl h-14 animate-pulse border border-gray-100" />)}
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 text-center py-16">
          <ShoppingCart className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="font-body text-gray-400">Nessun ordine trovato</p>
        </div>
      ) : (
        <>
          {/* ── DESKTOP: tabella ── */}
          <div className="hidden md:block bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['#','Cliente','Data','Articoli','Totale','Avanzamento','Stato','Azione',''].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-body text-xs text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {orders.map(order => {
                  const action     = STATUS_ACTIONS[order.status];
                  const isBusy     = updatingId === order.id;
                  const isTerminal = ['completed','cancelled'].includes(order.status);
                  const items      = parseItems(order.items);
                  const isExp      = expandedId === order.id;

                  return (
                    <>
                      <tr key={order.id} className={`hover:bg-gray-50/50 transition-colors ${isExp ? 'bg-primary/2' : ''}`}>
                        {/* # */}
                        <td className="px-4 py-3">
                          <span className="font-body text-xs text-gray-400 font-mono">
                            #{order.id.slice(-6).toUpperCase()}
                          </span>
                        </td>
                        {/* Cliente */}
                        <td className="px-4 py-3">
                          <p className="font-body font-semibold text-gray-900 text-sm">{order.customer_name}</p>
                          <p className="font-body text-xs text-gray-400">{order.customer_phone}</p>
                        </td>
                        {/* Data */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="font-body text-xs text-gray-500">{fmtDate(order.created_at)}</span>
                        </td>
                        {/* Articoli */}
                        <td className="px-4 py-3 text-center">
                          <span className="font-body text-xs font-semibold text-gray-700">{items.length}</span>
                        </td>
                        {/* Totale */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="font-body text-sm font-bold text-primary">{fmtPrice(order.total)}</span>
                        </td>
                        {/* Pipeline */}
                        <td className="px-4 py-3">
                          <MiniPipeline status={order.status} />
                        </td>
                        {/* Stato */}
                        <td className="px-4 py-3">
                          <StatusBadge status={order.status} />
                        </td>
                        {/* Azione */}
                        <td className="px-4 py-3">
                          {action && !isTerminal ? (
                            <button
                              onClick={() => handleAdvance(order)}
                              disabled={isBusy}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-dark hover:bg-primary text-white rounded-lg font-body text-xs font-semibold transition-colors disabled:opacity-60 whitespace-nowrap"
                            >
                              <action.icon className="w-3 h-3" />
                              {action.label}
                            </button>
                          ) : isTerminal ? (
                            <span className="font-body text-xs text-gray-400">—</span>
                          ) : null}
                        </td>
                        {/* Icone */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Link to={`/admin/ordini/${order.id}`}
                              className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors" title="Dettaglio">
                              <Eye className="w-3.5 h-3.5" />
                            </Link>
                            <button onClick={() => setLabelState({ order, showWhatsApp: false })}
                              className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors" title="Etichetta">
                              <Printer className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => setExpanded(isExp ? null : order.id)}
                              className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 transition-colors">
                              {isExp ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            </button>
                            <button onClick={() => setDeleteId(order.id)}
                              className="p-1.5 hover:bg-red-50 rounded-lg text-gray-300 hover:text-red-400 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Riga espansa */}
                      {isExp && (
                        <tr key={`${order.id}-exp`} className="bg-gray-50/50">
                          <td colSpan={9} className="px-6 py-4">
                            <div className="grid md:grid-cols-2 gap-6">
                              {/* Articoli */}
                              <div>
                                <p className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Articoli</p>
                                <div className="space-y-1.5">
                                  {items.map((item, i) => (
                                    <div key={i} className="flex items-center gap-2.5 bg-white rounded-lg p-2 border border-gray-100">
                                      {item.product_image && (
                                        <img src={item.product_image} alt={item.product_name}
                                          className="w-8 h-8 object-cover rounded-md flex-shrink-0"
                                          onError={e => { e.target.style.display = 'none'; }} />
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <p className="font-body text-xs font-medium text-gray-900 truncate">{item.product_name}</p>
                                        <p className="font-body text-xs text-gray-400">×{item.quantity}{item.price > 0 ? ` · ${fmtPrice(item.price * item.quantity)}` : ''}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                {order.total > 0 && (
                                  <div className="flex justify-between font-body font-semibold text-gray-900 text-sm mt-2 pt-2 border-t border-gray-200">
                                    <span>Totale</span><span className="text-primary">{fmtPrice(order.total)}</span>
                                  </div>
                                )}
                              </div>
                              {/* Dati cliente */}
                              <div>
                                <p className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Cliente</p>
                                <div className="bg-white rounded-lg p-3 border border-gray-100 space-y-1 font-body text-sm">
                                  <p className="font-semibold text-gray-900">{order.customer_name}</p>
                                  <a href={`tel:${order.customer_phone}`} className="block text-primary text-xs">{order.customer_phone}</a>
                                  {order.customer_email && <a href={`mailto:${order.customer_email}`} className="block text-primary text-xs truncate">{order.customer_email}</a>}
                                  {order.customer_address && <p className="text-gray-600 text-xs">{order.customer_address}</p>}
                                </div>
                                {order.notes && (
                                  <div className="mt-2 bg-amber-50 border border-amber-100 rounded-lg p-2.5">
                                    <p className="font-body text-xs text-amber-700">{order.notes}</p>
                                  </div>
                                )}
                                {!isTerminal && (
                                  <button onClick={() => changeStatus(order.id, 'cancelled')} disabled={isBusy}
                                    className="mt-2 flex items-center gap-1.5 text-red-500 hover:text-red-700 font-body text-xs font-medium transition-colors disabled:opacity-50">
                                    <XCircle className="w-3.5 h-3.5" /> Annulla ordine
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── MOBILE: cards ── */}
          <div className="md:hidden space-y-2">
            {orders.map(order => {
              const action     = STATUS_ACTIONS[order.status];
              const isBusy     = updatingId === order.id;
              const isTerminal = ['completed','cancelled'].includes(order.status);
              const items      = parseItems(order.items);
              const isExp      = expandedId === order.id;

              return (
                <div key={order.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                  <div className="px-3 py-3">
                    {/* Row 1 */}
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <StatusBadge status={order.status} />
                        <MiniPipeline status={order.status} />
                      </div>
                      <div className="flex items-center gap-0.5">
                        <Link to={`/admin/ordini/${order.id}`} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400">
                          <Eye className="w-4 h-4" />
                        </Link>
                        <button onClick={() => setExpanded(isExp ? null : order.id)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400">
                          {isExp ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        <button onClick={() => setDeleteId(order.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-300 hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {/* Row 2 */}
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="font-body font-semibold text-gray-900 text-sm truncate">{order.customer_name}</p>
                      {order.total > 0 && <span className="font-heading font-bold text-primary text-sm flex-shrink-0">{fmtPrice(order.total)}</span>}
                    </div>
                    {/* Row 3 */}
                    <div className="flex items-center justify-between mt-0.5">
                      <p className="font-body text-xs text-gray-400 truncate">{order.customer_phone}</p>
                      <p className="font-body text-xs text-gray-400 ml-2 flex-shrink-0">{items.length} art. · {fmtDate(order.created_at)}</p>
                    </div>
                    {/* Azione */}
                    {action && !isTerminal && (
                      <button onClick={() => handleAdvance(order)} disabled={isBusy}
                        className="mt-2.5 w-full flex items-center justify-center gap-2 bg-primary-dark hover:bg-primary text-white py-2 rounded-lg font-body text-xs font-semibold transition-colors disabled:opacity-60">
                        <action.icon className="w-3.5 h-3.5" />{action.label}<ArrowRight className="w-3.5 h-3.5 ml-auto" />
                      </button>
                    )}
                  </div>

                  {/* Dettaglio espanso mobile */}
                  {isExp && (
                    <div className="border-t border-gray-100 px-3 pb-3 pt-2.5 space-y-3">
                      <div className="space-y-1.5">
                        {items.map((item, i) => (
                          <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                            {item.product_image && (
                              <img src={item.product_image} alt={item.product_name} className="w-8 h-8 object-cover rounded-md flex-shrink-0"
                                onError={e => { e.target.style.display = 'none'; }} />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-body text-xs font-medium text-gray-900 truncate">{item.product_name}</p>
                              <p className="font-body text-xs text-gray-400">×{item.quantity}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2.5 space-y-0.5">
                        <p className="font-body text-sm font-semibold text-gray-900">{order.customer_name}</p>
                        <a href={`tel:${order.customer_phone}`} className="block font-body text-xs text-primary">{order.customer_phone}</a>
                        {order.customer_email && <a href={`mailto:${order.customer_email}`} className="block font-body text-xs text-primary truncate">{order.customer_email}</a>}
                        {order.customer_address && <p className="font-body text-xs text-gray-600">{order.customer_address}</p>}
                      </div>
                      {order.notes && <div className="bg-amber-50 border border-amber-100 rounded-lg p-2.5"><p className="font-body text-xs text-amber-700">{order.notes}</p></div>}
                      <div className="flex gap-2">
                        <button onClick={() => setLabelState({ order, showWhatsApp: false })}
                          className="flex-1 flex items-center justify-center gap-1.5 border border-gray-200 text-gray-600 py-2 rounded-lg font-body text-xs transition-colors">
                          <Printer className="w-3.5 h-3.5" /> Etichetta
                        </button>
                        {!isTerminal && (
                          <button onClick={() => changeStatus(order.id, 'cancelled')} disabled={isBusy}
                            className="flex-1 flex items-center justify-center gap-1.5 border border-red-200 text-red-600 py-2 rounded-lg font-body text-xs transition-colors">
                            <XCircle className="w-3.5 h-3.5" /> Annulla
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Conferma eliminazione */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="font-heading text-lg font-bold text-gray-900 mb-2">Elimina Ordine</h3>
            <p className="font-body text-gray-500 text-sm mb-6">Sei sicuro? L'azione è irreversibile.</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setDeleteId(null)} className="flex-1 rounded-xl">Annulla</Button>
              <Button onClick={() => handleDelete(deleteId)} className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl">Elimina</Button>
            </div>
          </div>
        </div>
      )}

      {/* Label modal */}
      {labelState && (
        <OrderLabelModal order={labelState.order} showWhatsApp={labelState.showWhatsApp} onClose={() => setLabelState(null)} />
      )}
    </div>
  );
}
